<template>
  <div>
    <page-header title="Geração manual de contratos">
      <template #description>
        Lista de billings que precisam de configuração manual de contrato —
        casos em que a automação não conseguiu gerar o contrato. Busque
        por instituição, selecione um billing pendente e finalize a
        configuração manualmente.
      </template>
    </page-header>

    <loading v-show="loading" class="cards__loading" full-screen="false" size="small" />

    <div v-show="!loading">
      <div class="row">
        <div class="col-md-8">
          <div class="input-group search-institution-field">
            <input
              type="text"
              class="form-control"
              placeholder="Pesquisar pelo nome da instituição"
              v-model="institutionSearch"
              @keyup.enter="searchInstitution()"
            >
            <span class="input-group-btn">
              <button
                class="btn btn-default"
                type="button"
                @click="searchInstitution()"
              >
                Pesquisar
              </button>
            </span>
          </div>
        </div>
      </div>

      <br>

      {{ totalItems }} resultados

      <div v-for="billing in pendingBillings" :key="billing.id">
        <div class="row">
          <div class="col-md-8">
            <div class="panel panel-default">
              <div class="panel-heading">
                <div class="heading-manual-config__wrapper">
                  <div>
                    <h4 class="heading-manual-config__title">
                      <router-link :to="{ path: `alunos/${billing.student_id}` }">
                        <strong>{{ billing.student_name }}</strong>
                      </router-link>
                      <span
                        class="label label-white label-warning student-view__header-tag"
                        v-if="billing.origin === 'from_parcelamento'"
                      >
                        Parcelamento
                      </span>
                    </h4>
                    {{ billing.cpf }}
                  </div>
                  <div>
                    <strong>{{ billing.id }}</strong>
                  </div>
                </div>
              </div>
              <div class="panel-body">
                <div class="table-responsive">
                  <table class="table table-hover table-spaced table-manual-config">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Curso</strong>
                        </td>
                        <td>
                          {{ billing.course_name }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Instituição</strong>
                        </td>
                        <td>
                          {{ billing.institution }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Tipo</strong>
                        </td>
                        <td>
                          {{ billing.kind }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Turno</strong>
                        </td>
                        <td>
                          {{ billing.shift }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Nível</strong>
                        </td>
                        <td>
                          {{ billing.level }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Dia de vencimento</strong>
                        </td>
                        <td>
                          Todo dia {{ billing.due_day }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Total de mensalidades do curso</strong>
                        </td>
                        <td>
                          {{ billing.course_installments }} {{ getInstallmentLabel(billing) }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Valor da parcela</strong>
                        </td>
                        <td>
                          {{ billing.installment_value_with_discount | formatCurrency }}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Canal aberto?</strong>
                        </td>
                        <td>
                          <span v-if="billing.installment_value_undefined">Sim</span>
                          <span v-else>Não</span>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Motivo para geração manual</strong>
                        </td>
                        <td>
                          {{ getHumanizedManualCreationReason(billing) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="text-right">
                  <router-link
                    :to="{ path: `criar-contrato-manual/${billing.id}` }"
                    class="btn btn-primary"
                    v-if="isManualContractCreationAllowed(billing)"
                  >
                    Configurar contrato
                  </router-link>

                  <p v-else>
                    Favor configurar instituição e encaminhar para o time de suporte criar este contrato.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <pagination ref="pagination" :total-pages="totalPages" @page-changed="onPageChange" />
    </div>
  </div>
</template>
<script>
import AdminService from 'services/AdminService';
import Loading from 'components/Loading';
import Pagination from 'components/Pagination';

export default {
  components: {
    Loading,
    Pagination,
  },
  data() {
    return {
      institutionSearch: null,
      pendingBillings: [],
      loading: true,
      totalItems: 0,
      totalPages: 0,
      currentPage: 1,
    };
  },
  methods: {
    getInstallmentLabel(billing) {
      if (billing.course_installments === 1) {
        return 'parcela';
      }
      return 'parcelas';
    },
    loadPendingBillings() {
      this.loading = true;
      const params = {
        institution_name: this.institutionSearch,
        page: this.currentPage,
      };

      AdminService.getBillingsPendingManualConfig(params)
        .then(({ data }) => {
          this.pendingBillings = data.billings;
          this.totalPages = data.total_pages;
          this.totalItems = data.total_items;
        })
        // eslint-disable-next-line no-unused-vars
        .catch((error) => {
          this.$alert.notifyAlert('Erro ao carregar os billings');
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => this.loading = false);
    },
    isManualContractCreationAllowed(billing) {
      const blockedReasons = ['configuration_missing', 'no_matching_business_rule_group', 'split_rule_missing'];
      return billing.metadata && blockedReasons.indexOf(billing.metadata.manual_creation_reason) < 0;
    },
    getHumanizedManualCreationReason(billing) {
      if (billing.metadata && billing.metadata.manual_creation_reason) {
        const reason = billing.metadata.manual_creation_reason;

        switch (reason) {
          case 'unknown_special_condition_received':
            return 'Condição especial desconhecida';
          case 'installment_value_undefined':
            return 'Oferta em canal aberto';
          case 'retroactive_start':
            return 'Matrícula tardia';
          case 'no_matching_business_rule_group':
            return 'Grupo de regras de negócio não encontrado';
          case 'contract_creation_rule_missing':
            return 'Regra de criação de contrato faltando';
          case 'due_day_rule_missing':
            return 'Regra de definição de dias de vencimento faltando';
          case 'split_rule_missing':
            return 'Regra de split faltando';
          case 'configuration_missing':
            return 'Configuração faltando';
          default:
            return reason;
        }
      }

      return '--';
    },
    searchInstitution() {
      this.resetPagination();
      this.loadPendingBillings();
    },
    resetPagination() {
      this.currentPage = 1;
      this.$refs.pagination.reset();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadPendingBillings();
    },
  },
  mounted() {
    this.loadPendingBillings();
  },
};
</script>
